import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { alpha, Box, IconButton, Menu, MenuItem } from '@mui/material';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';
import { ActionMenuItem } from 'components/common/action-menu/ActionMenu';
import { IRecentChat } from 'api/concierge';
import { highlightMatches } from './highlight';
import { ICustomStyles } from '../index';
import defaultStyles from './styles';

export default function RecentChatRow({
    chat,
    query,
    onOpen,
    onPin,
    onRename,
    onDelete,
}: {
    chat: IRecentChat;
    query: string;
    onOpen: (chat: IRecentChat) => void;
    onPin: (chat: IRecentChat) => void;
    onRename: (chat: IRecentChat) => void;
    onDelete: (chat: IRecentChat) => void;
}) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.recentChatsItem, 'row');
    }, [customStyles]);
    const act = (action: (chat: IRecentChat) => void) => () => {
        setAnchor(null);
        action(chat);
    };

    return (
        <Box
            data-test={`concierge_recent_chat_${chat.conversation_ts}`}
            sx={styles.row}
            className={anchor ? 'menu-open' : undefined}
        >
            <Box
                component="button"
                type="button"
                onClick={() => onOpen(chat)}
                title={chat.title}
                data-test={`concierge_recent_chat_open_${chat.conversation_ts}`}
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    p: 0,
                    border: 0,
                    background: 'none',
                    textAlign: 'left',
                    font: 'inherit',
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: 'primary.main',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' },
                }}
            >
                {highlightMatches(chat.title, query)}
            </Box>
            <IconButton
                className="recent-chat-kebab"
                size="small"
                aria-label={t('recent_chats.actions.menu')}
                onClick={(event) => setAnchor(event.currentTarget)}
                data-test={`concierge_recent_chat_menu_${chat.conversation_ts}`}
                // Hidden by opacity, not visibility, so the button stays in the tab order.
                sx={{ opacity: 0, p: '2px', color: 'text.primary' }}
            >
                <IconMi icon="more" fontSize="16" />
            </IconButton>
            <Menu
                open={null !== anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                MenuListProps={{ dense: true, sx: { py: 0.5, minWidth: '120px' } }}
                // The panel sits at modal + 1 (MI-30425), so its menus go one above it.
                sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
                // Rendered in a portal outside the panel, so recentChats selectors cannot reach it.
                slotProps={{
                    backdrop: { sx: { backgroundColor: 'transparent' } },
                    paper: { sx: customStyles?.recentChatsMenu },
                }}
            >
                <MenuItem sx={{ p: 0, minHeight: '32px' }} data-test="concierge_recent_chat_pin" onClick={act(onPin)}>
                    <ActionMenuItem
                        dense
                        icon="thumbtack"
                        label={t(chat.pinned ? 'recent_chats.actions.unpin' : 'recent_chats.actions.pin')}
                    />
                </MenuItem>
                <MenuItem
                    sx={{ p: 0, minHeight: '32px' }}
                    data-test="concierge_recent_chat_rename"
                    onClick={act(onRename)}
                >
                    <ActionMenuItem dense icon="edit-pencil" label={t('recent_chats.actions.rename')} />
                </MenuItem>
                <Box
                    sx={{ height: '1px', my: 0.5, backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) }}
                />
                <MenuItem
                    sx={{ p: 0, minHeight: '32px' }}
                    data-test="concierge_recent_chat_delete"
                    onClick={act(onDelete)}
                >
                    <ActionMenuItem dense icon="trash" label={t('recent_chats.actions.delete')} />
                </MenuItem>
            </Menu>
        </Box>
    );
}
